import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Youtube, RefreshCw, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function YouTubeSyncCard({ onSynced }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["enh-youtube"], queryFn: () => api.get("/enhancer/me/youtube").then((r) => r.data) });
  const [channels, setChannels] = useState("");
  const [touched, setTouched] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!touched && data) {
      const list = data.youtube_channels?.length ? data.youtube_channels : (data.youtube_channel ? [data.youtube_channel] : []);
      setChannels(list.join("\n"));
    }
  }, [data, touched]);

  const configured = data?.configured;

  const sync = async () => {
    const list = channels.split("\n").map((c) => c.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Add at least one YouTube channel or playlist."); return; }
    setSyncing(true);
    try {
      const { data: res } = await api.post("/enhancer/me/youtube/sync", { youtube_channels: list, limit: 10 });
      if (res.imported > 0) toast.success(`Imported ${res.imported} new video${res.imported === 1 ? "" : "s"} from ${res.sources} source${res.sources === 1 ? "" : "s"}.`);
      else toast.success("You're up to date — no new videos found.");
      if (res.errors?.length) toast.error(`Some sources had issues: ${res.errors[0]}`);
      qc.invalidateQueries({ queryKey: ["enh-youtube"] });
      onSynced?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const count = channels.split("\n").map((c) => c.trim()).filter(Boolean).length;

  return (
    <div className="eyw-card p-5 mb-5 border-l-4 border-l-[#FF0000]/70" data-testid="youtube-sync-card">
      <div className="flex items-center gap-2 mb-1"><Youtube size={20} className="text-[#FF0000]" /><h3 className="font-serif text-xl text-[#1A242B]">YouTube Sync</h3></div>
      {!configured && (
        <p className="text-sm text-[#4A5D6B] mb-3">YouTube Sync isn't switched on yet. Add your channels/playlists below — an admin just needs to add a YouTube API key in Integrations to start pulling videos in automatically.</p>
      )}
      {configured && (
        <p className="text-sm text-[#4A5D6B] mb-3">Add one or more channels or playlists — <b>one per line</b>. We'll pull the latest videos from each into your profile and the media library.</p>
      )}
      <textarea
        value={channels}
        onChange={(e) => { setChannels(e.target.value); setTouched(true); }}
        data-testid="youtube-channel-input"
        rows={3}
        placeholder={"@yourchannel\nhttps://youtube.com/@secondchannel\nhttps://youtube.com/playlist?list=PLxxxx"}
        className="eyw-input w-full resize-none text-sm mb-2"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-[#8FA89B]">{count} source{count === 1 ? "" : "s"} · channels &amp; playlists supported</span>
        <button onClick={sync} disabled={syncing || !configured} title={!configured ? "Waiting for an admin to add a YouTube API key" : ""} data-testid="youtube-sync-btn" className="eyw-btn !py-2.5 disabled:opacity-60">
          {syncing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Sync now
        </button>
      </div>
      {data?.last_synced_at && <p className="text-xs text-[#8FA89B] mt-2 flex items-center gap-1"><CheckCircle2 size={12} className="text-[#5C8A72]" /> Last synced {new Date(data.last_synced_at).toLocaleString()} · {data.synced_count || 0} imported all-time</p>}
    </div>
  );
}
